// The Metric Value EVIDENCE SHEET, loaded ON DEMAND (N-BOARD-2 AC-4, G-011). It fetches the stored evidence
// for one Value id — method, sources, calculation narrative, confidence, limitations, inclusion/exclusion,
// and the truncation flag — so a human can AUDIT the number without re-deriving it. It renders exactly the
// stored evidence projection (P-SELF — nothing recomputed) with NO internal identifier (G-197). Tokens only
// (G-181). Fetched via useProjection (the same injectable transport the surfaces use, hermetic in tests).

import { useCallback } from 'react';
import type { MetricEvidenceSheet } from '../projections.ts';
import { useProjection } from '../useProjection.ts';
import { SurfaceFrame } from '../surface-frame.tsx';

export interface EvidenceSheetProps {
  readonly valueId: string;
  readonly load: (valueId: string) => Promise<MetricEvidenceSheet>;
}

/** A row in the evidence sheet — a mono label and its stored value. */
function Row({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 py-1.5">
      <span className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle">
        {label}
      </span>
      <span className="text-sm text-text-muted">{children}</span>
    </div>
  );
}

/** The on-demand evidence panel for one Metric Value. */
export function EvidenceSheet({ valueId, load }: EvidenceSheetProps): React.JSX.Element {
  // Memoise the loader for this value id so the hook re-reads only when the id changes.
  const loadThis = useCallback(() => load(valueId), [load, valueId]);
  const state = useProjection<MetricEvidenceSheet>(loadThis);

  return (
    <div className="mt-3 rounded-md border border-border bg-surface-sunken px-4 py-3">
      <SurfaceFrame state={state}>
        {(evidence) => (
          <div className="divide-y divide-border">
            <Row label="Method">{evidence.method}</Row>
            <Row label="Sources">{evidence.sources.join(', ')}</Row>
            <Row label="Calculation">{evidence.calculationNarrative}</Row>
            <Row label="Confidence">{evidence.confidence}</Row>
            <Row label="Limitations">{evidence.limitations}</Row>
            <Row label="Scope">{evidence.inclusionExclusion}</Row>
            {evidence.truncated ? (
              <Row label="Note">Some detail was truncated in the stored evidence.</Row>
            ) : null}
          </div>
        )}
      </SurfaceFrame>
    </div>
  );
}
